/* Push-Einstellungen: Zustand für den Einstellungsdialog und der Abgleich der
 * Sprache.
 *
 * Zustand im Modulbereich (Singleton), wie bei den anderen Composables.
 *
 * Schalter speichern sofort und optimistisch: Der Schalter springt um, das
 * Speichern läuft, bei einem Fehler springt er zurück und der Dialog zeigt
 * eine allgemeine Meldung. Anders als bei Spielerdaten gibt es hier keine
 * gleichzeitige Bearbeitung durch mehrere Personen — jeder ändert nur seine
 * eigene Zeile.
 *
 * Die Sprache wird nicht im Dialog gewählt, sondern mitgeführt: Beim Anmelden
 * und bei jedem Sprachwechsel schreibt die App die aktuelle Sprache in die
 * eigene Zeile (useLanguageSync, aufgerufen in App.vue). Die Edge Function
 * baut den Push-Text danach in dieser Sprache.
 */

import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from './useAuth.js';
import { loadOwnPushPreferences, saveOwnPushPreferences } from '../supabase/pushPreferences.js';
import { withTypeEnabled, isTypeEnabled, receivesPush, pushLanguageFor } from '../domain/notifications.js';

const disabledTypes = ref([]);
const loading = ref(false);
const loaded = ref(false);
const error = ref(null);
let loadedFor = null;
/* Zuletzt gespeicherte Sprache je Nutzer — verhindert Schreiben ohne Änderung. */
let syncedKey = null;

export function usePushPreferences() {
  const { user } = useAuth();

  async function load() {
    const id = user.value?.id;
    if (!id) return;
    if (loadedFor === id && loaded.value) return;
    loading.value = true;
    error.value = null;
    try {
      const prefs = await loadOwnPushPreferences(id);
      disabledTypes.value = prefs.disabledTypes;
      loaded.value = true;
      loadedFor = id;
    } catch (e) {
      error.value = e.message || 'loadFailed';
    } finally {
      loading.value = false;
    }
  }

  function isEnabled(type) {
    return isTypeEnabled(disabledTypes.value, type);
  }

  /** Schalter umlegen; bei Fehler zurücksetzen und die Fehlermeldung setzen. */
  async function setEnabled(type, enabled) {
    const id = user.value?.id;
    if (!id) return;
    const before = disabledTypes.value;
    const next = withTypeEnabled(before, type, enabled);
    disabledTypes.value = next;
    error.value = null;
    try {
      await saveOwnPushPreferences(id, { disabledTypes: next });
    } catch (e) {
      disabledTypes.value = before;
      error.value = e.message || 'saveFailed';
    }
  }

  return { disabledTypes, loading, loaded, error, load, isEnabled, setEnabled };
}

/**
 * Sprache der App in die eigene Zeile schreiben — beim Anmelden und bei jedem
 * Sprachwechsel. Nur für Rollen, die überhaupt Push bekommen. Scheitert das
 * (z. B. die Tabelle ist auf dem Server noch nicht angelegt), bleibt das ohne
 * Folgen für die App: Der Push kommt dann eben auf Englisch.
 *
 * In der Setup-Funktion einer Komponente aufrufen (braucht useI18n).
 */
export function useLanguageSync() {
  const { user, role } = useAuth();
  const { locale } = useI18n();

  watch(
    [() => user.value?.id, () => role.value, locale],
    async ([id, r, lang]) => {
      if (!id) {
        syncedKey = null;
        return;
      }
      if (!receivesPush(r)) return;
      const key = `${id}:${lang}`;
      if (key === syncedKey) return;
      try {
        await saveOwnPushPreferences(id, { language: pushLanguageFor(lang) });
        syncedKey = key;
      } catch {
        /* nur Konsole (siehe supabase/pushPreferences.js); nächster Versuch
           beim nächsten Sprachwechsel oder Anmelden */
      }
    },
    { immediate: true }
  );
}
